<template>
  <q-input
    ref="input"
    stack-label
    hide-bottom-space
    :debounce="debounce"
    :model-value="visibleDate"
    :class="{'required-astrx-container': required}"
    :label="label"
    :outlined="outlined"
    :dense="dense"
    :prefix="prefix"
    :suffix="suffix"
    :hint="hint"
    :readonly="readonly"
    :disable="disable"
    :error="error"
    :placeholder="placeholder"
    :clearable="clearable && !mobileMode"
    :error-message="errorMessage"
    @clear="onClearDate"
    @update:model-value="onInputDate"
    @focus="onFocusDate"
    @keydown="onKeydownDate"
    @keyup="onKeyupDate"
  >
    <responsive-menu
      ref="menu"
      :breakpoint="1024"
      :toolbar-title="mobileLabel"
      :no-parent-event="true"
      :no-refocus="true"
      force-on-mobile
    >
      <slot name="append" />
      <q-item-label
        v-if="hasAppendSlot"
        header
        class="text-bold q-mt-md"
      >
        Custom Date
      </q-item-label>
      <q-date
        :model-value="modelValue"
        :options="dateCheckFn"
        :first-day-of-week="startOfWeek"
        minimal
        flat
        mask="YYYY-MM-DD"
        class="bg-transparent"
        @update:model-value="(val) => onInputDate(val, true)"
      />
      <q-toolbar>
        <q-btn
          v-if="clearable && !!modelValue"
          size="12px"
          label="Clear"
          flat
          @click="onClearDate"
        />
        <q-space />
        <q-btn
          v-close-popup
          size="12px"
          label="Close"
          flat
        />
      </q-toolbar>
    </responsive-menu>
  </q-input>
</template>

<script>
/**
 * DatePicker acts similar to default QInput component, including props
 *
 * SLOTS:
 * - append: content to appear at the top of the popup dialog
 *
 * SPECIAL EMITS:
 * - mobile-input: emit when a date is picked in mobile mode
 */
export default {
  props: {
    modelValue: {
      type: String,
      default: null
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    required: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: undefined
    },
    suffix: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Enter Date...'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    // Minimum date
    min: {
      type: String,
      default: '1900-01-01'
    },
    // Maximum date
    max: {
      type: String,
      default: '2100-01-01'
    },
    // How long after input should the model value be updated (default 0s - no delay)
    debounce: {
      type: Number,
      default: 0
    },
    // Label to show on mobile devices within the popup
    mobileLabel: {
      type: String,
      default: 'Select Date'
    }
  },
  emits: ['update:modelValue', 'clear', 'mobileInput'],
  data () {
    return {
      localValue: this.modelValue,
      hasBeenInvalid: false
    }
  },
  computed: {
    startOfWeek () {
      return this.$store.getters['company/getCompany'].calendar_start_of_week || 0
    },
    hasAppendSlot () {
      return !!this.$slots.append
    },
    mobileMode () {
      return this.windowWidth <= 1024 || this.$q.platform.is.mobile
    },
    visibleDate () {
      if (!this.modelValue) {
        return null
      }
      return this.$formatDate(this.modelValue, this.luxonFormats.SHORT_WORDED_DATE)
    }
  },
  watch: {
    'modelValue' (val) {
      this.localValue = val
    }
  },
  methods: {
    setModelValue () {
      this.hasBeenInvalid = false
      this.$emit('update:modelValue', this.localValue)
      if (!this.localValue) {
        this.$emit('clear')
      }
    },
    /**
     * Main method for checking a date is valid setting the value locally
     * or to the model
     */
    onInputDate (val, updateModel = false) {
      if (this.mobileMode && !updateModel) {
        return
      }

      if (this.mobileMode && updateModel) {
        if (this.$refs.menu) {
          this.$refs.menu.close()
        }
        this.$emit('mobileInput')
      }

      if (!val) {
        if (this.clearable) {
          this.localValue = null
          if (updateModel) {
            this.setModelValue()
          }
          return
        }
        else if (this.modelValue) {
          val = this.luxon.fromSQL(this.modelValue).toSQLDate()
        }
        else {
          val = this.luxon.today().toSQLDate()
        }
      }

      val = this.luxon.fromJSDate(new Date(val))
      if (!val.isValid) {
        this.hasBeenInvalid = true
        return
      }

      // There is a bug in some browsers where if the date string doesn't have a year,
      // it will default to 2001.
      // If this happens, we will try and make it get the closest appropriate date from the current date
      if (this.hasBeenInvalid && val.year === 2001) {
        val = val.set({ year: this.luxon.now().year })
        if (val < this.luxon.now().minus({ months: 6 })) {
          val = val.add(1, 'year')
        }
      }

      if (this.max && val > this.luxon.fromSQL(this.max)) {
        val = this.luxon.fromSQL(this.max)
      }
      else if (this.min && val < this.luxon.fromSQL(this.min)) {
        val = this.luxon.fromSQL(this.min)
      }

      val = val.toSQLDate()
      this.localValue = val
      if (updateModel) {
        this.setModelValue()
      }
    },
    /**
     * Clear date
     */
    onClearDate () {
      this.onInputDate(null, true)
    },
    /**
     * Filter date picker values that can be selected
     * @param date
     * @returns {boolean|boolean}
     */
    dateCheckFn (date) {
      date = this.luxon.fromFormat(date, 'yyyy/MM/dd')
      return (!this.max || date <= this.luxon.fromSQL(this.max)) &&
        (!this.min || date >= this.luxon.fromSQL(this.min))
    },
    /**
     * Keyup does not work for Tab as it will have focused on a new element.
     * Use keydown to set the local value upon tabbing
     */
    onKeydownDate (e) {
      if (!['Tab'].includes(e.key)) {
        return
      }

      if (e.key === 'Tab') {
        if (this.localValue) {
          this.onInputDate(this.localValue, true)
        }
        this.onBlurDate()
      }
    },
    /**
     * Check the key that was pressed and set the date accordingly
     */
    onKeyupDate (e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
        return
      }

      const date = !this.localValue ? this.luxon.now() : this.luxon.fromSQL(this.localValue)
      if (!date.isValid) {
        return
      }

      if (e.key === 'ArrowDown') {
        this.onInputDate(date.minus({ day: 1 }).toSQLDate(), true)
      }
      else if (e.key === 'ArrowUp') {
        this.onInputDate(date.plus({ day: 1 }).toSQLDate(), true)
      }
      else if (e.key === 'Enter') {
        this.onBlurDate()
        this.$nextTick(() => {
          this.$refs.input.blur()
        })
      }
    },
    onFocusDate () {
      if (this.$refs.menu) {
        this.$refs.menu.open()
      }
    },
    onBlurDate () {
      this.setModelValue()
      if (this.$refs.menu) {
        this.$refs.menu.close()
      }
    },
    focus () {
      this.$refs.input.focus()
    }
  }
}
</script>
